import { resolveButtonSizePadding } from "./blocks/button-presentation";
import { clampBlockWidthPx, resolveRowLayoutWidth } from "./block-width";
import { stripHtmlTags } from "./html-text";
import {
  columnAvailableWidth,
  MIN_COLUMN_PX,
  MIN_COLUMN_PERCENT,
  normalizePercents,
  percentMinFromPx,
  rebalancePercentsToFitContentMins,
  resolveColumnPixelWidths,
} from "./column-presets";
import { isGroup, isRow } from "./sections";
import type {
  Block,
  ButtonBlock,
  ButtonRowBlock,
  ButtonRowSlot,
  Column,
  EmailDocument,
  EmailFontFamily,
  Row,
  SocialBlock,
  TextBlock,
} from "./types";

function horizontalPadding(padding: { left: number; right: number }): number {
  return padding.left + padding.right;
}

let measureCanvas: HTMLCanvasElement | null = null;

function measureLabelWidthPx(
  label: string,
  fontSize: number,
  fontWeight: number,
  fontFamily: EmailFontFamily,
): number {
  const text = stripHtmlTags(label);
  if (text.length === 0) {
    return 0;
  }

  if (typeof document !== "undefined") {
    measureCanvas ??= document.createElement("canvas");
    const context = measureCanvas.getContext("2d");
    if (context) {
      context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      return Math.ceil(context.measureText(text).width);
    }
  }

  return Math.ceil(text.length * fontSize * (fontWeight >= 700 ? 0.58 : 0.52));
}

function buttonSlotIntrinsicWidthPx(
  slot: ButtonRowSlot,
  fontFamily: EmailFontFamily,
): number {
  const { x } = resolveButtonSizePadding(slot.size);
  const labelWidth = measureLabelWidthPx(
    slot.label,
    slot.fontSize,
    slot.fontWeight,
    fontFamily,
  );
  return labelWidth + x * 2 + slot.borderWidth * 2;
}

function buttonSlotMinWidthPx(
  slot: ButtonRowSlot,
  fontFamily: EmailFontFamily,
): number {
  if (slot.fullWidth) {
    return 0;
  }

  return buttonSlotIntrinsicWidthPx(slot, fontFamily);
}

function buttonBlockIntrinsicWidthPx(
  block: ButtonBlock,
  fontFamily: EmailFontFamily,
): number {
  const { x } = resolveButtonSizePadding(block.size);
  const labelWidth = measureLabelWidthPx(
    block.text,
    block.fontSize,
    block.fontWeight,
    fontFamily,
  );
  return (
    labelWidth +
    x * 2 +
    block.borderWidth * 2 +
    horizontalPadding(block.padding)
  );
}

function buttonBlockMinWidthPx(
  block: ButtonBlock,
  fontFamily: EmailFontFamily,
): number {
  if (block.fullWidth) {
    return 0;
  }

  return buttonBlockIntrinsicWidthPx(block, fontFamily);
}

function buttonRowBlockIntrinsicWidthPx(
  block: ButtonRowBlock,
  fontFamily: EmailFontFamily,
): number {
  let width = 0;
  for (let index = 0; index < block.buttons.length; index += 1) {
    const slot = block.buttons[index];
    if (!slot) {
      continue;
    }
    if (width > 0) {
      width += block.gap;
    }
    width += buttonSlotIntrinsicWidthPx(slot, fontFamily);
  }

  if (width === 0) {
    return 0;
  }

  return width + horizontalPadding(block.padding);
}

function buttonRowBlockMinWidthPx(
  block: ButtonRowBlock,
  fontFamily: EmailFontFamily,
): number {
  let width = 0;
  for (let index = 0; index < block.buttons.length; index += 1) {
    const slot = block.buttons[index];
    if (!slot || slot.fullWidth) {
      continue;
    }
    if (width > 0) {
      width += block.gap;
    }
    width += buttonSlotMinWidthPx(slot, fontFamily);
  }

  if (width === 0) {
    return 0;
  }

  return width + horizontalPadding(block.padding);
}

function socialBlockMinWidthPx(block: SocialBlock): number {
  const count = block.links.length;
  if (count === 0) {
    return 0;
  }

  return (
    count * block.iconSize +
    (count - 1) * block.gap +
    horizontalPadding(block.padding)
  );
}

function textBlockOverflowWidthPx(block: TextBlock): number {
  if (block.widthPx == null) {
    return 0;
  }

  return (
    clampBlockWidthPx(block.widthPx, Number.MAX_SAFE_INTEGER) +
    horizontalPadding(block.padding)
  );
}

function textBlockMinWidthPx(): number {
  return 0;
}

function blockContentMinWidthPx(
  block: Block,
  fontFamily: EmailFontFamily,
): number {
  switch (block.type) {
    case "button":
      return buttonBlockMinWidthPx(block, fontFamily);
    case "buttonRow":
      return buttonRowBlockMinWidthPx(block, fontFamily);
    case "social":
      return socialBlockMinWidthPx(block);
    case "text":
      return textBlockMinWidthPx();
    default:
      return 0;
  }
}

function blockDesktopOverflowWidthPx(
  block: Block,
  fontFamily: EmailFontFamily,
): number {
  switch (block.type) {
    case "button":
      return buttonBlockIntrinsicWidthPx(block, fontFamily);
    case "buttonRow":
      return buttonRowBlockIntrinsicWidthPx(block, fontFamily);
    case "social":
      return socialBlockMinWidthPx(block);
    case "text":
      return textBlockOverflowWidthPx(block);
    default:
      return 0;
  }
}

export type ColumnBlockOverflowIssue = {
  blockId: string;
  columnIndex: number;
  blockOverflowPx: number;
  columnContentPx: number;
};

function resolveColumnContentWidthPx(
  row: Row,
  columnIndex: number,
  innerWidth: number,
): number {
  const column = row.columns[columnIndex];
  if (!column) {
    return 0;
  }

  const pixelWidths = resolveColumnPixelWidths(
    row.columnWidths,
    innerWidth,
    row.columnGap,
  );
  const columnPixelWidth = pixelWidths[columnIndex] ?? 0;
  return Math.max(
    0,
    columnPixelWidth - horizontalPadding(column.padding),
  );
}

export function resolveRowColumnOverflowIssues(
  row: Row,
  innerWidth: number,
  fontFamily: EmailFontFamily,
): ColumnBlockOverflowIssue[] {
  if (row.columns.length <= 1) {
    return [];
  }

  const issues: ColumnBlockOverflowIssue[] = [];
  row.columns.forEach((column, columnIndex) => {
    const columnContentPx = resolveColumnContentWidthPx(
      row,
      columnIndex,
      innerWidth,
    );
    for (const block of column.blocks) {
      const blockOverflowPx = blockDesktopOverflowWidthPx(block, fontFamily);
      if (blockOverflowPx > columnContentPx) {
        issues.push({
          blockId: block.id,
          columnIndex,
          blockOverflowPx,
          columnContentPx,
        });
      }
    }
  });

  return issues;
}

export function rowHasColumnBlockOverflow(
  row: Row,
  innerWidth: number,
  fontFamily: EmailFontFamily,
): boolean {
  return resolveRowColumnOverflowIssues(row, innerWidth, fontFamily).length > 0;
}

export function blockHasColumnOverflow(
  row: Row,
  innerWidth: number,
  fontFamily: EmailFontFamily,
  blockId: string,
): boolean {
  return resolveRowColumnOverflowIssues(row, innerWidth, fontFamily).some(
    issue => issue.blockId === blockId,
  );
}

export function blockHasColumnOverflowInDocument(
  document: EmailDocument,
  rowId: string,
  blockId: string,
): boolean {
  const layout = resolveRowLayoutWidth(document, rowId);
  if (!layout) {
    return false;
  }

  return blockHasColumnOverflow(
    layout.row,
    layout.innerWidth,
    document.settings.bodyFontFamily,
    blockId,
  );
}

export function resolveDocumentColumnOverflowBlockIds(
  document: EmailDocument,
): ReadonlyMap<string, ReadonlySet<string>> {
  const overflowByRow = new Map<string, Set<string>>();

  for (const section of document.sections) {
    const rows = isGroup(section) ? section.rows : isRow(section) ? [section] : [];
    for (const row of rows) {
      const layout = resolveRowLayoutWidth(document, row.id);
      if (!layout || row.columns.length <= 1) {
        continue;
      }

      const issues = resolveRowColumnOverflowIssues(
        layout.row,
        layout.innerWidth,
        document.settings.bodyFontFamily,
      );
      if (issues.length === 0) {
        continue;
      }

      overflowByRow.set(
        row.id,
        new Set(issues.map(issue => issue.blockId)),
      );
    }
  }

  return overflowByRow;
}

export function resolveColumnContentMinWidthPx(
  column: Column,
  fontFamily: EmailFontFamily,
): number {
  let contentMin = MIN_COLUMN_PX;
  for (const block of column.blocks) {
    contentMin = Math.max(contentMin, blockContentMinWidthPx(block, fontFamily));
  }
  return contentMin + horizontalPadding(column.padding);
}

export function resolveRowColumnMinWidthPercents(
  row: Row,
  innerWidth: number,
  fontFamily: EmailFontFamily,
): number[] {
  const available = columnAvailableWidth(
    innerWidth,
    row.columnGap,
    row.columns.length,
  );
  return row.columns.map(column =>
    percentMinFromPx(resolveColumnContentMinWidthPx(column, fontFamily), available),
  );
}

export function columnMaxWidthPercent(
  minPercents: readonly number[],
  columnIndex: number,
): number {
  const othersMin = minPercents.reduce(
    (total, min, index) => (index === columnIndex ? total : total + min),
    0,
  );
  return Math.max(minPercents[columnIndex] ?? MIN_COLUMN_PERCENT, 100 - othersMin);
}

export function rebalanceRowColumnWidthsForContent(
  row: Row,
  innerWidth: number,
  fontFamily: EmailFontFamily,
): boolean {
  if (row.columns.length <= 1) {
    return false;
  }

  const minPercents = resolveRowColumnMinWidthPercents(
    row,
    innerWidth,
    fontFamily,
  );
  const nextWidths = normalizePercents(
    rebalancePercentsToFitContentMins(row.columnWidths, minPercents),
  );
  const changed = nextWidths.some(
    (width, index) => width !== row.columnWidths[index],
  );
  if (!changed) {
    return false;
  }

  row.columnWidths = nextWidths;
  return true;
}

export function applyRowColumnContentRebalance(
  document: EmailDocument,
  rowId: string,
): void {
  const layout = resolveRowLayoutWidth(document, rowId);
  if (!layout) {
    return;
  }

  rebalanceRowColumnWidthsForContent(
    layout.row,
    layout.innerWidth,
    document.settings.bodyFontFamily,
  );
}
